<template>
  <div class="assign-professional-modal-body">
    <v-card class="modal-card" flat>
      <v-toolbar class="modal-toolbar" density="comfortable" flat>
        <div class="modal-bar">
          <v-btn icon variant="text" class="back-btn" aria-label="Back" @click="modal.close()">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="modal-title">Assign professional</div>
        </div>
      </v-toolbar>

      <v-card-text class="body py-6">
        <div class="hint">
          Select a professional to assign to this appointment.
        </div>

        <div v-if="filteredProviders.length" class="provider-list">
          <div
            v-for="provider in filteredProviders"
            :key="String(getProviderId(provider))"
            class="provider-card"
            :class="{ 'provider-card--selected': selectedProviderId === String(getProviderId(provider)) }"
            role="button"
            tabindex="0"
            @click="selectProvider(provider)"
            @keydown.enter.prevent="selectProvider(provider)"
            @keydown.space.prevent="selectProvider(provider)"
          >
            <v-avatar size="40" color="primary" variant="tonal" class="provider-avatar">
              <img
                v-if="getProviderAvatarUrl(provider)"
                :src="getProviderAvatarUrl(provider)"
                alt="Provider avatar"
                loading="lazy"
              />
              <span v-else class="provider-initials">{{ getProviderInitials(provider) }}</span>
            </v-avatar>
            <div class="provider-meta">
              <div class="provider-title">{{ getProviderTitle(provider) }}</div>
              <div class="provider-subtitle">{{ getProviderSubtitle(provider) }}</div>
            </div>
          </div>
        </div>
        <div v-else class="text-muted text-body-2">
          No providers found.
        </div>
      </v-card-text>

      <v-card-actions class="actions">
        <div v-if="assignError" class="assign-error text-caption">
          {{ assignError }}
        </div>
        <AppButtonFlat
          block
          prepend-icon="mdi-account-check-outline"
          :loading="isAssigning"
          :disabled="!selectedProviderId || isAssigning"
          @click="handleAssigned"
        >
          Assign
        </AppButtonFlat>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { useModalStore } from "@/shared/stores/modal";
import { ref } from "vue";
import { assignAppointmentServiceProvider } from "@/shared/services/appointments.api";

const props = defineProps<{
  appointment: Record<string, any>;
  providers: any[];
  onAssigned?: (
    appointment: Record<string, any>,
    provider: any | null
  ) => void;
}>();

const modal = useModalStore();
const providers = props.providers;

const selectedProviderId = ref<string>("");
const isAssigning = ref(false);
const assignError = ref("");

function getProviderId(p: any): string | number {
  return p?.vendor_user_id ?? p?.id ?? p?.user_id ?? p?.staff_id ?? p?._id ?? p?.client_id ?? p?.email ?? p?.name;
}

function getProviderTitle(p: any): string {
  return String(p?.name ?? p?.full_name ?? p?.display_name ?? "Professional");
}

function getProviderAvatarUrl(p: any): string {
  return String(
    p?.avatar ??
      p?.primary_avatar ??
      p?.profile?.avatar ??
      p?.client?.avatar ??
      p?.client?.primary_avatar ??
      ""
  ).trim();
}

function getProviderInitials(p: any): string {
  const name = getProviderTitle(p).trim();
  if (!name) return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return (first + last).toUpperCase() || "?";
}

function getProviderSubtitle(p: any): string {
  const email = String(p?.email ?? "").trim();
  const phone = String(p?.phone ?? "").trim();
  return [email, phone].filter(Boolean).join(" • ");
}

const filteredProviders = providers;

function selectProvider(provider: any) {
  selectedProviderId.value = String(getProviderId(provider));
}

async function handleAssigned() {
  if (!selectedProviderId.value || isAssigning.value) return;

  assignError.value = "";
  isAssigning.value = true;

  const provider =
    filteredProviders.find((p) => String(getProviderId(p)) === selectedProviderId.value) || null;
  const appointmentId = props.appointment?.id ?? props.appointment?.appointment_id;
  const providerId = provider?.vendor_user_id ?? provider?.id;
  try {
    if (!appointmentId) throw new Error("Missing appointment id");
    if (!providerId) throw new Error("Missing provider id");

    await assignAppointmentServiceProvider(appointmentId, { provider_id: providerId });
    props.onAssigned?.(props.appointment, provider);
    modal.close();
  } catch (err: any) {
    assignError.value =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      "Failed to assign professional";
  } finally {
    isAssigning.value = false;
  }
}
</script>

<style scoped>
.assign-professional-modal-body {
  padding: 0 0 calc(env(safe-area-inset-bottom, 0px) + 16px);
  min-height: 100dvh;
}

.modal-card {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.modal-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-theme);
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 0 0;
  height: auto;
}

.modal-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
}

:deep(.modal-toolbar .v-toolbar__content) {
  height: auto;
  min-height: 64px;
  align-items: center;
  padding: 8px 16px;
}

.back-btn {
  width: 50px;
  height: 50px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.hint {
  color: var(--color-subtitle, #475569);
  font-size: 14px;
  margin-bottom: 12px;
}

.body {
  flex: 1;
  overflow: auto;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 12px);
}

.provider-list {
  padding-bottom: 12px;
}

.actions {
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 12px);
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.assign-error {
  color: #ef4444;
}

.provider-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  outline: none;
  display: flex;
  align-items: center;
  gap: 12px;
}

.provider-card--selected {
  border-color: var(--color-primary, #2563eb);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.provider-card:focus-visible {
  border-color: var(--color-primary, #2563eb);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.provider-avatar {
  flex: 0 0 auto;
}

.provider-initials {
  font-weight: 700;
  color: var(--color-primary, #2563eb);
}

.provider-meta {
  min-width: 0;
  flex: 1;
}

.provider-title {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.provider-subtitle {
  font-size: 12px;
  color: var(--color-subtitle, #475569);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
